import { format, isAfter } from 'date-fns'
import { AlertTriangle, Clock } from 'lucide-react'
import { FC } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Admin, AdminRole } from '@/out/admin'
import { Invitation } from '@/out/invitation'
import { updateAdminRole } from '@/thunks/admins/role'
import { updateInvitationRole } from '@/thunks/invitations/role'

import ArchiveAdminDialog from './Admins/ArchiveDialog'
import RevokeInvitationDialog from './Invitations/RevokeDialog'
import RoleSelect from './Shared/RoleSelect'

type AdminManagementItem = (Admin & { type: 'admin' }) | (Invitation & { type: 'invitation' })

interface AdminRowProps {
  item: AdminManagementItem
  onRoleChange: (id: string, role: AdminRole) => void
  onArchive?: (id: string) => void
  onRevoke?: (id: string) => void
}

const getInitials = (firstName?: string, lastName?: string) => {
  if (!firstName && !lastName) return 'U'
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()
}

const AdminRow: FC<AdminRowProps> = ({ item, onRoleChange, onArchive, onRevoke }) => {
  const isAdmin = item.type === 'admin'
  const displayName = isAdmin && item.lastName ? `${item.firstName} ${item.lastName}` : 'Unknown'

  return (
    <Card key={item.id} data-id={item.id} data-testid={`admin-row-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center flex-1 gap-4">
            <div className="flex items-center justify-center w-10 h-10 text-sm font-medium rounded-full bg-primary/10 text-primary">
              {isAdmin ? getInitials(item.firstName, item.lastName) : getInitials(item.email)}
            </div>
            <div className="flex-1 min-w-0">
              {isAdmin && item.lastName ? (
                <>
                  <div className="text-sm font-medium">{displayName}</div>
                  <div className="text-sm truncate text-muted-foreground">{item.email}</div>
                </>
              ) : (
                <div className="text-sm font-medium">{item.email}</div>
              )}
            </div>
          </div>

          {item.type === 'invitation' && (
            <div className="flex items-center px-4">
              {(() => {
                const isValid = isAfter(new Date(item.expiresAt), new Date())
                const expiryDate = format(new Date(item.expiresAt), 'MMM d, yyyy')
                return (
                  <Badge
                    variant={isValid ? 'secondary' : 'destructive'}
                    className={`text-xs whitespace-nowrap font-normal ${
                      isValid
                        ? 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-50'
                        : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-50'
                    }`}
                  >
                    {isValid ? <Clock className="w-3 h-3 mr-2" /> : <AlertTriangle className="w-3 h-3 mr-2" />}
                    {isValid ? 'Expires' : 'Expired'} {expiryDate}
                  </Badge>
                )
              })()}
            </div>
          )}

          <div className="flex items-center gap-4">
            <div className="min-w-[120px]">
              {isAdmin && item.role === 'owner' ? (
                <Badge variant="default">Account Owner</Badge>
              ) : (
                <div data-testid={`${item.type}-role-select-container`}>
                  <RoleSelect
                    admin={item}
                    thunk={isAdmin ? updateAdminRole : updateInvitationRole}
                    onChange={role => onRoleChange(item.id, role)}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && item.role !== 'owner' && onArchive && (
                <ArchiveAdminDialog admin={item} onArchive={() => onArchive(item.id)} />
              )}
              {!isAdmin && onRevoke && <RevokeInvitationDialog invitation={item} onRevoke={() => onRevoke(item.id)} />}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default AdminRow
